import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  HiCalendar,
  HiClock,
  HiCurrencyDollar,
  HiLightningBolt,
  HiQrcode,
  HiUsers,
} from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { IconType } from "react-icons";

interface BannerFeature {
  id: string;
  icon: IconType;
  translationKey: string;
}

const BANNER_FEATURES: BannerFeature[] = [
  {
    id: "qr-tickets",
    icon: HiQrcode,
    translationKey: "components.eventPayments.banner.qrTickets",
  },
  {
    id: "date-management",
    icon: HiCalendar,
    translationKey: "components.eventPayments.banner.dateManagement",
  },
  {
    id: "capacity-limits",
    icon: HiUsers,
    translationKey: "components.eventPayments.banner.capacityLimits",
  },
  {
    id: "early-bird",
    icon: HiClock,
    translationKey: "components.eventPayments.banner.earlyBirdPricing",
  },
  {
    id: "quick-setup",
    icon: HiLightningBolt,
    translationKey: "components.eventPayments.banner.quickSetup",
  },
  {
    id: "flexible-pricing",
    icon: HiCurrencyDollar,
    translationKey: "components.eventPayments.banner.flexiblePricing",
  },
];

interface FeatureCardProps {
  feature: BannerFeature;
}

const FeatureCard: React.FC<FeatureCardProps> = memo(({ feature }) => {
  const { t } = useTranslation();
  const Icon = feature.icon;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-50">
          <Icon className="text-gray-600" size={18} aria-hidden="true" />
        </div>
        <h4 className="text-xs font-medium text-gray-700">
          {t(feature.translationKey)}
        </h4>
      </div>
    </div>
  );
});

FeatureCard.displayName = "FeatureCard";

const EventBanner: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-600"
          aria-hidden="true"
        >
          <HiTicket size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("components.eventPayments.banner.title")}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("components.eventPayments.banner.subtitle")}
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6"
        role="list"
        aria-label={t("components.eventPayments.banner.title") as string}
      >
        {BANNER_FEATURES.map((feature) => (
          <FeatureCard key={feature.id} feature={feature} />
        ))}
      </div>
    </div>
  );
};

export default memo(EventBanner);
